import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * OnboardingTutorial - Pop-up tutoriel pour les nouveaux utilisateurs
 * Affiche 2-3 slides d'informations avec navigation et validation
 * S'adapte au thème clair/sombre
 * Le statut "vu" est sauvegardé en base de données pour persister entre sessions
 */
const OnboardingTutorial = ({ 
  pageId,           // Identifiant unique de la page (ex: "watchlist", "history", "dca")
  slides,           // Array de { icon, title, description }
  onComplete,       // Callback quand l'utilisateur termine
  storageKey,       // Clé pour mémoriser si déjà vu
  forceShow = false // Force l'affichage (pour le Guide)
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const { user, loading: authLoading } = useAuth();
  
  // Check theme from localStorage or default to dark
  const [isDark, setIsDark] = useState(() => {
    const savedTheme = localStorage.getItem('tradex_theme');
    return savedTheme !== 'light'; // Default to dark
  });

  useEffect(() => {
    // Listen for theme changes
    const checkTheme = () => {
      const savedTheme = localStorage.getItem('tradex_theme');
      setIsDark(savedTheme !== 'light');
    };
    
    // Check on mount and set up interval for changes
    checkTheme();
    const interval = setInterval(checkTheme, 500);
    
    return () => clearInterval(interval);
  }, []);

  const checkTutorialStatus = useCallback(async () => {
    // If forceShow, always display
    if (forceShow) {
      setIsOpen(true);
      setIsLoading(false);
      return;
    }

    const key = storageKey || `tutorial_${pageId}`;
    const seenLocal = localStorage.getItem(key) === 'true';

    // Not authenticated → the tutorial is meaningless for a guest (they can't
    // interact anyway) and would obscure the preview interface. Skip entirely.
    if (!user) {
      setIsLoading(false);
      return;
    }

    // Authenticated → union of DB and localStorage. Reasoning:
    //   Some dismissals persist to localStorage synchronously but the DB POST
    //   may be aborted (browser close, network drop). Trusting only the DB
    //   would re-open the tutorial next session even though the user has
    //   clearly dismissed it. If either side says "seen", we treat it as seen
    //   and self-heal the divergent side silently.
    try {
      const res = await axios.get(`${API}/api/user/tutorial-status`);
      const seenTutorials = Array.isArray(res.data?.seen_tutorials) ? res.data.seen_tutorials : [];
      const seenInDb = seenTutorials.includes(key);

      if (seenInDb) {
        // DB is authoritative → also cache locally for next cold start.
        if (!seenLocal) localStorage.setItem(key, 'true');
      } else if (seenLocal) {
        // Local says seen, DB missed it → re-sync silently, do NOT re-open.
        try {
          await axios.post(`${API}/api/user/tutorial-seen`, { tutorial_key: key });
        } catch (_) { /* non-critical */ }
      } else {
        // Truly first time on this tutorial → show it.
        setIsOpen(true);
      }
    } catch (error) {
      // API unreachable → fall back to localStorage (never re-show a tutorial
      // the user has already dismissed locally).
      if (!seenLocal) setIsOpen(true);
    }
    setIsLoading(false);
  }, [pageId, storageKey, forceShow, user]);

  useEffect(() => {
    // Wait for auth to resolve before deciding anything — otherwise `user`
    // is still null and we'd fall into the "guest" branch even for a logged-in user.
    if (authLoading) return;
    checkTutorialStatus();
  }, [authLoading, checkTutorialStatus]);

  const markTutorialAsSeen = async () => {
    const key = storageKey || `tutorial_${pageId}`;

    // Always save to localStorage synchronously first — survives even if the
    // browser is closed before the network POST completes.
    localStorage.setItem(key, 'true');

    // Best-effort sync to DB. Cookies are sent automatically via
    // axios.defaults.withCredentials = true (set in AuthContext). No need to
    // stuff the localStorage 'cookie' marker into an Authorization header.
    if (user) {
      try {
        await axios.post(`${API}/api/user/tutorial-seen`, { tutorial_key: key });
      } catch (error) {
        console.error('Error saving tutorial status:', error);
      }
    }
  };

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const handlePrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleComplete = async () => {
    if (!forceShow) {
      await markTutorialAsSeen();
    }
    setIsOpen(false);
    setCurrentSlide(0);
    onComplete?.();
  };

  const handleSkip = async () => {
    if (!forceShow) {
      await markTutorialAsSeen();
    }
    setIsOpen(false);
    setCurrentSlide(0);
  };

  if (isLoading || !isOpen || !slides || slides.length === 0) return null;

  const currentSlideData = slides[currentSlide];
  const isLastSlide = currentSlide === slides.length - 1;
  const IconComponent = currentSlideData.icon;

  return (
    <>
      {/* Full screen overlay - completely opaque */}
      <div 
        className="fixed inset-0 z-[9998]"
        style={{ backgroundColor: isDark ? '#000000' : '#f5f5f5' }}
        onClick={handleSkip}
      />
      
      {/* Centered modal container */}
      <div 
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '100vh',
          minWidth: '100vw'
        }}
      >
        {/* Modal */}
        <div 
          className={`relative w-full max-w-md rounded-2xl border shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300 ${
            isDark 
              ? 'bg-gray-900 border-white/10' 
              : 'bg-white border-gray-200'
          }`}
          style={{ margin: 'auto' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button 
            onClick={handleSkip}
            className={`absolute top-4 right-4 p-2 rounded-full transition-colors z-10 ${
              isDark 
                ? 'text-gray-400 hover:text-white hover:bg-white/10' 
                : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            <X className="w-5 h-5" />
          </button>

          {/* Content */}
          <div className="p-8 pt-12">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className={`w-20 h-20 rounded-2xl flex items-center justify-center ${
                isDark 
                  ? 'bg-emerald-500/20 border border-emerald-500/30' 
                  : 'bg-emerald-50 border border-emerald-200'
              }`}>
                {IconComponent && (
                  <IconComponent className={`w-10 h-10 ${
                    isDark ? 'text-emerald-400' : 'text-emerald-600'
                  }`} />
                )}
              </div>
            </div>

            {/* Title */}
            <h2 className={`text-2xl font-bold text-center mb-3 ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              {currentSlideData.title}
            </h2>

            {/* Description */}
            <p className={`text-center leading-relaxed mb-8 ${
              isDark ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {currentSlideData.description}
            </p>

            {/* Dots indicator */}
            <div className="flex justify-center gap-2 mb-6">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentSlide 
                      ? `w-6 ${isDark ? 'bg-emerald-400' : 'bg-emerald-500'}` 
                      : `w-2 ${isDark ? 'bg-gray-600 hover:bg-gray-500' : 'bg-gray-300 hover:bg-gray-400'}`
                  }`}
                />
              ))}
            </div>

            {/* Navigation buttons */}
            <div className="space-y-3">
              {/* Main action button */}
              {isLastSlide ? (
                <Button
                  onClick={handleComplete}
                  className="w-full bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white font-semibold h-12"
                >
                  <Check className="w-5 h-5 mr-2" />
                  Compris !
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  className="w-full font-semibold h-12 bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 text-white"
                >
                  Suivant
                  <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              )}

              {/* Previous button - only show if not on first slide */}
              {currentSlide > 0 && (
                <Button
                  variant="ghost"
                  onClick={handlePrev}
                  className={`w-full ${
                    isDark 
                      ? 'text-gray-400 hover:text-white hover:bg-white/5' 
                      : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
                  }`}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Précédent
                </Button>
              )}
            </div>

            {/* Skip link */}
            <button
              onClick={handleSkip}
              className={`w-full mt-4 text-sm transition-colors ${
                isDark 
                  ? 'text-gray-500 hover:text-gray-400' 
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              Passer le tutoriel
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default OnboardingTutorial;
